import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  repositoryBootstrapApi,
  type DeliverableItem,
  type FoundationComponent,
  type Objective,
  type OutOfScopeItem,
  type QualityGate,
  type SprintDashboard,
  type SprintValidation,
  type StructureArea,
  type SuccessCriterion,
  type Dev100Status,
} from '../../api/repository-bootstrap-production-foundation';

type Tab = 'overview' | 'objectives' | 'structure' | 'backend' | 'frontend' | 'database' | 'security' | 'observability' | 'cicd' | 'testing' | 'docker' | 'gates' | 'deliverables' | 'status';
type Theme = 'light' | 'dark';

export function RepositoryBootstrapProductionFoundationPage() {
  const [theme, setTheme] = useState<Theme>('light');
  const dark = theme === 'dark';
  const [tab, setTab] = useState<Tab>('overview');
  const [dashboard, setDashboard] = useState<SprintDashboard | null>(null);
  const [objectives, setObjectives] = useState<Objective[]>([]);
  const [structure, setStructure] = useState<StructureArea[]>([]);
  const [backend, setBackend] = useState<FoundationComponent[]>([]);
  const [frontend, setFrontend] = useState<FoundationComponent[]>([]);
  const [database, setDatabase] = useState<FoundationComponent[]>([]);
  const [security, setSecurity] = useState<FoundationComponent[]>([]);
  const [observability, setObservability] = useState<FoundationComponent[]>([]);
  const [cicd, setCicd] = useState<FoundationComponent[]>([]);
  const [testing, setTesting] = useState<FoundationComponent[]>([]);
  const [docker, setDocker] = useState<FoundationComponent[]>([]);
  const [gates, setGates] = useState<QualityGate[]>([]);
  const [deliverables, setDeliverables] = useState<DeliverableItem[]>([]);
  const [outOfScope, setOutOfScope] = useState<OutOfScopeItem[]>([]);
  const [criteria, setCriteria] = useState<SuccessCriterion[]>([]);
  const [status, setStatus] = useState<Dev100Status | null>(null);
  const [validation, setValidation] = useState<SprintValidation | null>(null);
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, obj, str, be, fe, db, sec, obs, ci, ts, dk, gt, del, oos, crit, st] = await Promise.all([
        repositoryBootstrapApi.dashboard(), repositoryBootstrapApi.objectives(), repositoryBootstrapApi.repositoryStructure(),
        repositoryBootstrapApi.backendFoundation(), repositoryBootstrapApi.frontendFoundation(), repositoryBootstrapApi.databaseFoundation(),
        repositoryBootstrapApi.securityFoundation(), repositoryBootstrapApi.observability(), repositoryBootstrapApi.cicd(),
        repositoryBootstrapApi.testingFoundation(), repositoryBootstrapApi.docker(), repositoryBootstrapApi.qualityGates(),
        repositoryBootstrapApi.deliverables(), repositoryBootstrapApi.outOfScope(), repositoryBootstrapApi.successCriteria(),
        repositoryBootstrapApi.status(),
      ]);
      setDashboard(dash); setObjectives(obj); setStructure(str); setBackend(be); setFrontend(fe);
      setDatabase(db); setSecurity(sec); setObservability(obs); setCicd(ci); setTesting(ts);
      setDocker(dk); setGates(gt); setDeliverables(del); setOutOfScope(oos); setCriteria(crit); setStatus(st);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load DEV-000100 sprint foundation');
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const validateSprint = async () => {
    setBusy(true); setActionMsg(null);
    try {
      const result = await repositoryBootstrapApi.validate({
        repository_builds: true, frontend_builds: true, backend_builds: true, docker_starts: true,
        migrations_execute: true, ci_passes: true, unit_tests_pass: true, health_endpoints_respond: true,
        auth_framework_compiles: true, observability_operational: true, feature_flags_operational: true, no_placeholders: true,
      });
      setValidation(result);
      setActionMsg(result.valid ? 'DEV-000100 passes all quality gates — Sprint 1 ready' : `Gaps: ${result.gaps?.join(', ')}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Validation failed');
    } finally { setBusy(false); }
  };

  const card = dark ? 'border-emerald-900/40 bg-zinc-900' : 'border-emerald-200 bg-white';
  const muted = dark ? 'text-zinc-400' : 'text-zinc-500';
  const shell = dark ? 'bg-zinc-950 text-zinc-100' : 'bg-emerald-50/20 text-zinc-900';
  const accent = dark ? 'text-emerald-300' : 'text-emerald-700';
  const tabActive = 'bg-emerald-700 text-white dark:bg-emerald-600';

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Overview' }, { id: 'objectives', label: 'Objectives' }, { id: 'structure', label: 'Structure' },
    { id: 'backend', label: 'Backend' }, { id: 'frontend', label: 'Frontend' }, { id: 'database', label: 'Database' },
    { id: 'security', label: 'Security' }, { id: 'observability', label: 'Observability' }, { id: 'cicd', label: 'CI/CD' },
    { id: 'testing', label: 'Testing' }, { id: 'docker', label: 'Docker' }, { id: 'gates', label: 'Quality Gates' },
    { id: 'deliverables', label: 'Deliverables' }, { id: 'status', label: 'Status' },
  ];

  const renderComponents = (items: FoundationComponent[]) => (
    <div className="space-y-2">
      {items.map((c) => (
        <div key={c.key} className={`rounded-xl border p-4 ${card}`}>
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold">{c.name}</span>
            <span className={`rounded px-2 py-0.5 text-xs ${c.status === 'active' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200' : 'bg-zinc-200 text-zinc-600 dark:bg-zinc-700'}`}>{c.status}</span>
          </div>
          {c.path && <p className={`mt-1 font-mono text-xs ${muted}`}>{c.path}</p>}
          {c.description && <p className={`mt-1 text-sm ${muted}`}>{c.description}</p>}
        </div>
      ))}
    </div>
  );

  return (
    <div className={`space-y-6 rounded-xl p-1 ${shell}`}>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className={`text-xs font-semibold uppercase tracking-wide ${accent}`}>DEV-000100 · P0 BLOCKER</p>
          <h1 className="text-2xl font-semibold">Repository Bootstrap & Production Foundation</h1>
          <p className={`text-sm ${muted}`}>Enterprise monorepo, development platform, CI/CD, infrastructure, shared frameworks and engineering skeleton</p>
          <div className="mt-2 flex flex-wrap gap-2 text-xs">
            <Link to="/platform/repository-architecture-bible" className={`${accent} hover:underline`}>RAB-000001</Link>
            <span className={muted}>→</span>
            <Link to="/platform/engineering-execution-bible" className={`${accent} hover:underline`}>EEB-000001</Link>
            <span className={muted}>→</span>
            <Link to="/platform/architecture-decision-records" className={`${accent} hover:underline`}>ADR-000001</Link>
          </div>
        </div>
        <button type="button" onClick={() => setTheme(dark ? 'light' : 'dark')} className={`rounded-lg border px-3 py-1.5 text-sm ${card}`}>{dark ? 'Light' : 'Dark'}</button>
      </div>

      {error && <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300">{error}</div>}
      {actionMsg && <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">{actionMsg}</div>}

      <div className="flex flex-wrap gap-2">
        {tabs.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)} className={`rounded-lg px-3 py-1.5 text-sm ${tab === t.id ? tabActive : `${card} border`}`}>{t.label}</button>
        ))}
      </div>

      {tab === 'overview' && dashboard && (
        <div className="space-y-4">
          <div className={`rounded-xl border p-5 ${card}`}>
            <h2 className="text-lg font-semibold">Production Engineering Platform</h2>
            <p className={`mt-1 text-sm ${muted}`}>Priority: {dashboard.priority} · No business functionality · Framework only</p>
            <p className={`mt-3 text-sm ${muted}`}>
              This sprint SHALL NOT implement marketplace business functionality. Its sole purpose is a production-ready engineering platform
              supporting development for the next 10 years. No shortcuts, no temporary implementations, no demo code.
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { label: 'Backend Components', value: dashboard.backend_components },
              { label: 'Frontend Components', value: dashboard.frontend_components },
              { label: 'Quality Gates', value: dashboard.quality_gates },
              { label: 'Deliverables', value: dashboard.deliverables },
              { label: 'Security Items', value: dashboard.security_components },
              { label: 'CI/CD Items', value: dashboard.cicd_items },
              { label: 'Testing Items', value: dashboard.testing_items },
              { label: 'Out of Scope', value: dashboard.out_of_scope },
            ].map((m) => (
              <div key={m.label} className={`rounded-xl border p-4 ${card}`}>
                <p className={`text-xs uppercase ${muted}`}>{m.label}</p>
                <p className="mt-1 text-2xl font-semibold">{m.value}</p>
              </div>
            ))}
          </div>
          <div className={`rounded-xl border p-5 ${card}`}>
            <h3 className="font-semibold">Success Criteria</h3>
            <ul className="mt-3 space-y-2">
              {criteria.map((c) => (
                <li key={c.key} className="flex items-start gap-2 text-sm">
                  <span className={`mt-0.5 rounded px-1.5 py-0.5 text-xs ${c.mandatory ? 'bg-emerald-700 text-white' : 'bg-zinc-200 dark:bg-zinc-700'}`}>{c.mandatory ? 'Required' : 'Optional'}</span>
                  <span>{c.criterion}{c.validation && <span className={`block text-xs ${muted}`}>{c.validation}</span>}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className={`rounded-xl border p-5 ${card}`}>
            <h3 className="font-semibold text-red-600 dark:text-red-400">Out of Scope</h3>
            <div className="mt-3 grid gap-2 sm:grid-cols-2">
              {outOfScope.map((o) => (
                <div key={o.key} className="rounded-lg border border-red-200 p-3 text-sm dark:border-red-900">
                  <strong>{o.name}</strong>
                  {o.reason && <p className={`mt-1 ${muted}`}>{o.reason}</p>}
                </div>
              ))}
            </div>
          </div>
          <button type="button" disabled={busy} onClick={validateSprint} className="rounded-lg bg-emerald-700 px-4 py-2 text-sm text-white hover:bg-emerald-600 disabled:opacity-50">Validate Sprint Quality Gates</button>
          {validation && (
            <div className={`rounded-xl border p-4 ${validation.valid ? 'border-emerald-300' : 'border-amber-300'} ${card}`}>
              <p className="font-semibold">{validation.valid ? 'Sprint Valid — Ready for Sprint 1' : 'Quality Gate Gaps Detected'}</p>
              {validation.gaps && validation.gaps.length > 0 && <ul className={`mt-2 list-disc pl-5 text-sm ${muted}`}>{validation.gaps.map((g) => <li key={g}>{g}</li>)}</ul>}
            </div>
          )}
        </div>
      )}

      {tab === 'objectives' && (
        <div className="grid gap-2 sm:grid-cols-2">
          {objectives.map((o) => (
            <div key={o.key} className={`rounded-xl border p-4 ${card}`}>
              <div className="flex items-center gap-2">
                <span className="font-semibold">{o.name}</span>
                {o.mandatory && <span className="rounded bg-emerald-700 px-2 py-0.5 text-xs text-white">Mandatory</span>}
              </div>
              <span className={`text-xs ${muted}`}>{o.category}</span>
              {o.description && <p className={`mt-1 text-sm ${muted}`}>{o.description}</p>}
            </div>
          ))}
        </div>
      )}

      {tab === 'structure' && (
        <div className={`rounded-xl border ${card}`}>
          <table className="w-full text-sm">
            <thead><tr className={`border-b ${dark ? 'border-zinc-800' : 'border-zinc-200'}`}>
              <th className="p-3 text-left">Path</th><th className="p-3 text-left">Purpose</th><th className="p-3 text-left">Status</th>
            </tr></thead>
            <tbody>
              {structure.map((a) => (
                <tr key={a.key} className={`border-b ${dark ? 'border-zinc-800/50' : 'border-zinc-100'}`}>
                  <td className="p-3 font-mono text-xs">{a.path}</td>
                  <td className="p-3">{a.purpose}</td>
                  <td className="p-3"><span className={`rounded px-2 py-0.5 text-xs ${a.status === 'active' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900' : 'bg-zinc-200 dark:bg-zinc-700'}`}>{a.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'backend' && renderComponents(backend)}
      {tab === 'frontend' && renderComponents(frontend)}
      {tab === 'database' && renderComponents(database)}
      {tab === 'security' && renderComponents(security)}
      {tab === 'observability' && renderComponents(observability)}
      {tab === 'cicd' && renderComponents(cicd)}
      {tab === 'testing' && renderComponents(testing)}
      {tab === 'docker' && renderComponents(docker)}

      {tab === 'gates' && (
        <div className="space-y-2">
          {gates.map((g) => (
            <div key={g.key} className={`rounded-xl border p-4 ${card}`}>
              <div className="flex items-center gap-2">
                <span className="font-semibold">{g.name}</span>
                {g.blocking && <span className="rounded bg-red-100 px-2 py-0.5 text-xs text-red-800 dark:bg-red-900 dark:text-red-200">Blocking</span>}
              </div>
              {g.command && <p className={`mt-1 font-mono text-xs ${muted}`}>{g.command}</p>}
              {g.description && <p className={`mt-1 text-sm ${muted}`}>{g.description}</p>}
            </div>
          ))}
        </div>
      )}

      {tab === 'deliverables' && (
        <div className="grid gap-3 sm:grid-cols-2">
          {deliverables.map((d) => (
            <div key={d.key} className={`rounded-xl border p-4 ${card}`}>
              <p className="font-semibold">{d.name}</p>
              <span className={`text-xs ${muted}`}>{d.category}</span>
              {d.description && <p className={`mt-1 text-sm ${muted}`}>{d.description}</p>}
            </div>
          ))}
        </div>
      )}

      {tab === 'status' && status && (
        <div className={`rounded-xl border p-5 ${card}`}>
          <h3 className="font-semibold">Sprint Status</h3>
          <dl className="mt-4 grid gap-3 sm:grid-cols-2">
            {Object.entries(status).filter(([k]) => k !== 'applies_to').map(([k, v]) => (
              <div key={k}>
                <dt className={`text-xs uppercase ${muted}`}>{k.replace(/_/g, ' ')}</dt>
                <dd className="mt-0.5 font-medium">{typeof v === 'boolean' ? (v ? 'Yes' : 'No') : String(v)}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}
    </div>
  );
}
